/**
 * GitHub API client wrapper used by front-end for GitHub operations.
 */
import { StoryNode } from '../types';

const BASE = '/github-api';

function headers(token?: string): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['x-github-token'] = token;
    return h;
}

export interface GitHubRepo {
    id: number;
    name: string;
    full_name: string;
}

export async function getRepos(token?: string): Promise<GitHubRepo[]> {
    // Request all repositories the authenticated user can access (including private repos).
    // Use explicit visibility and affiliation params to ensure private and collaborator repos are included.
    const params = new URLSearchParams({
        sort: 'updated',
        per_page: '200',
        visibility: 'all',
        affiliation: 'owner,collaborator,organization_member',
    });

    const res = await fetch(`${BASE}/user/repos?${params.toString()}`, {
        headers: headers(token),
    });
    return res.json();
}

export async function graphql<T = unknown>(token: string | undefined, query: string, variables: Record<string, unknown> = {}): Promise<T> {
    const res = await fetch(`${BASE}/graphql`, {
        method: 'POST',
        headers: headers(token),
        body: JSON.stringify({ query, variables }),
    });
    return res.json();
}

/**
 * Return GitHub Projects V2 for repository/user, preferring repo-level first.
 */
export async function getProjectsV2(
    token: string | undefined,
    owner: string,
    repo: string
): Promise<Array<{ id: string; title: string; number: number }>> {
    const repoQuery = `query($owner:String!,$repo:String!){
    repository(owner:$owner,name:$repo){
      projectsV2(first:20){nodes{id title number}}
    }
  }`;
    const repoData = await graphql<{ data?: { repository?: { projectsV2?: { nodes?: Array<{ id: string; title: string; number: number }> } } } }>(token, repoQuery, { owner, repo });
    const repoProjects = (repoData?.data?.repository?.projectsV2?.nodes || []).filter(Boolean);
    if (repoProjects.length > 0) return repoProjects;

    const userQuery = `query{
    viewer{
      projectsV2(first:20){nodes{id title number}}
    }
  }`;
    const userData = await graphql<{ data?: { viewer?: { projectsV2?: { nodes?: Array<{ id: string; title: string; number: number }> } } } }>(token, userQuery);
    return (userData?.data?.viewer?.projectsV2?.nodes || []).filter(Boolean);
}

export async function addIssueToProjectV2(token: string | undefined, projectId: string, issueNodeId: string): Promise<unknown> {
    const mutation = `mutation($projectId:ID!,$contentId:ID!){
    addProjectV2ItemById(input:{projectId:$projectId,contentId:$contentId}){
      item{id}
    }
  }`;
    return graphql(token, mutation, { projectId, contentId: issueNodeId });
}

export interface GitHubIssueRequest {
    title: string;
    body: string;
    labels: string[];
}

export interface GitHubIssueResponse {
    html_url?: string;
    node_id?: string;
    message?: string;
}

export async function createIssue(token: string | undefined, owner: string, repo: string, story: StoryNode): Promise<GitHubIssueResponse> {
    const moscowLabel = story.moscow?.toLowerCase().replace(/\s+/g, '-') || '';
    const body = buildIssueBody(story);
    const res = await fetch(`${BASE}/repos/${owner}/${repo}/issues`, {
        method: 'POST',
        headers: headers(token),
        body: JSON.stringify({
            title: story.title,
            body,
            labels: moscowLabel ? [moscowLabel] : [],
        }),
    });
    return res.json();
}

function buildIssueBody(story: StoryNode): string {
    const lines: string[] = [];
    if (story.asA || story.iWant || story.soThat) {
        lines.push('## User story');
        if (story.asA) lines.push(`**As a** ${story.asA}`);
        if (story.iWant) lines.push(`**I want** ${story.iWant}`);
        if (story.soThat) lines.push(`**So that** ${story.soThat}`);
        lines.push('');
    }
    if (story.acceptanceCriteria) {
        lines.push('## Acceptance criteria');
        story.acceptanceCriteria
            .split('\n')
            .filter(Boolean)
            .forEach((ac) => {
                lines.push(`- [ ] ${ac.replace(/^[-*]\s*/, '')}`);
            });
        lines.push('');
    }
    if (story.moscow) {
        lines.push(`🎯 Priority: **${story.moscow}**`);
    }
    return lines.join('\n');
}

export async function ensureLabels(token: string | undefined, owner: string, repo: string): Promise<void> {
    const labels = [
        { name: 'must-have', color: '3B82F6' },
        { name: 'should-have', color: '22C55E' },
        { name: 'could-have', color: 'EAB308' },
        { name: "won't-have-(mvp)", color: '9CA3AF' },
    ];

    for (const label of labels) {
        await fetch(`${BASE}/repos/${owner}/${repo}/labels`, {
            method: 'POST',
            headers: headers(token),
            body: JSON.stringify(label),
        }).catch(() => {
            // ignore label creation failures
        });
    }
}

export async function deleteIssue(token: string | undefined, owner: string, repo: string, issueNumber: string): Promise<unknown> {
    const res = await fetch(`${BASE}/repos/${owner}/${repo}/issues/${issueNumber}`, {
        method: 'PATCH',
        headers: headers(token),
        body: JSON.stringify({ state: 'closed' }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as { message?: string }).message || 'Failed to close GitHub issue');
    return data;
}
